'use client';

import { useEffect, useState } from 'react';
import type { DoorCatalogue, DoorCatalogueSource } from './doorCatalogue';
import { useSpatialPorts } from './ports';

/**
 * The shop's door catalogue for a block, loaded once per source.
 * <ul>
 *   <li>`loading`: not answered yet; render a placeholder, not "not open";</li>
 *   <li>`ready`: the catalogue;</li>
 *   <li>`none`: the shop sells no doors (no source, or nothing published);</li>
 *   <li>`error`: the shop could not be reached; say so, and never treat it as "none".</li>
 * </ul>
 */
export type DoorCatalogueState =
  | { status: 'loading' }
  | { status: 'ready'; catalogue: DoorCatalogue }
  | { status: 'none' }
  | { status: 'error'; message: string };

function known(source: DoorCatalogueSource | undefined): DoorCatalogueState {
  if (!source) return { status: 'none' };
  const held = source.peek?.();
  if (held === undefined) return { status: 'loading' };
  return held ? { status: 'ready', catalogue: held } : { status: 'none' };
}

export function useDoorCatalogue(): DoorCatalogueState {
  const { doorCatalogue } = useSpatialPorts();
  const [state, setState] = useState<DoorCatalogueState>(() => known(doorCatalogue));

  useEffect(() => {
    const now = known(doorCatalogue);
    if (!doorCatalogue || now.status !== 'loading') {
      setState(now);
      return;
    }
    let current = true;
    setState({ status: 'loading' });
    doorCatalogue
      .load()
      .then((catalogue) => {
        if (current) setState(catalogue ? { status: 'ready', catalogue } : { status: 'none' });
      })
      .catch((error: unknown) => {
        if (current) setState({ status: 'error', message: error instanceof Error ? error.message : 'The door ranges could not be loaded.' });
      });
    return () => {
      current = false;
    };
  }, [doorCatalogue]);

  return state;
}
